import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';
import { useEmpresaPath } from '../hooks/useEmpresaPath';
import { pathWithEmpresa } from '../utils/companyUrl';
import empleadoService from '../services/empleadoService';
import ImportarEmpleados from '../components/ImportarEmpleados';
import AsignarContratoModal from '../components/AsignarContratoModal';
import contratosEmpleadoService from '../services/contratosEmpleadoService';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { MOTIVOS_CESE, MOTIVO_CESE_OTROS, motivoCeseEnLista, construirMotivoCese } from '../constants/motivosCese';

const obtenerNombreEmpresa = (empresa) =>
  empresa?.name || empresa?.nombre || empresa?.nombre_empresa || 'EMPRESA';

const obtenerDetalleEmpresa = (empresa) => {
  const texto = empresa?.description || empresa?.descripcion || empresa?.direccion || empresa?.address;
  return texto?.trim() || '';
};

const Empleados = () => {
  const { selectedCompany, empresaIdFromUrl } = useAuth();
  const { contratosActivados } = useNavigation();
  const nuevoEmpleadoPath = useEmpresaPath('/empleados/nuevo');
  const empresaActivaId = selectedCompany?.id || empresaIdFromUrl;
  
  // Estados principales
  const [empleadosCompletos, setEmpleadosCompletos] = useState([]); // Todos los empleados sin filtrar
  const [empleados, setEmpleados] = useState([]); // Empleados filtrados y paginados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState({ visible: false, tipo: '', texto: '' });

  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterCargo, setFilterCargo] = useState('');
  const [filterProyecto, setFilterProyecto] = useState('');
  const [sortField, setSortField] = useState('nombre_completo');
  const [sortDirection, setSortDirection] = useState('asc');

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmpleados, setTotalEmpleados] = useState(0);
  const [perPage] = useState(10);

  // Estados para sistema de bajas/altas
  const [showBajaModal, setShowBajaModal] = useState(false);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [empleadoParaBaja, setEmpleadoParaBaja] = useState(null);
  const [procesandoBaja, setProcesandoBaja] = useState(false);
  const [fechaBaja, setFechaBaja] = useState('');
  const [motivoBaja, setMotivoBaja] = useState('');
  const [motivoBajaDetalle, setMotivoBajaDetalle] = useState('');
  const [fechaReingreso, setFechaReingreso] = useState('');
  const [historialEditable, setHistorialEditable] = useState([]);
  const [editandoHistorial, setEditandoHistorial] = useState(null);
  const [showEditarHistorialModal, setShowEditarHistorialModal] = useState(false);
  const [fechaIngresoEdit, setFechaIngresoEdit] = useState('');
  const [fechaCeseEdit, setFechaCeseEdit] = useState('');
  const [motivoCeseEdit, setMotivoCeseEdit] = useState('');

  // Estados para reportes
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [empleadoParaReporte, setEmpleadoParaReporte] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);

  // Estados para importación
  const [showImportarModal, setShowImportarModal] = useState(false);

  // Estados para asignación de contratos
  const [showAsignarContratoModal, setShowAsignarContratoModal] = useState(false);
  const [contratoSoloHistorial, setContratoSoloHistorial] = useState(false);
  const [empleadoParaContrato, setEmpleadoParaContrato] = useState(null);
  const [contratosPorEmpleado, setContratosPorEmpleado] = useState({});

  // Cargar empleados al montar el componente o cuando cambie la empresa
  useEffect(() => {
    if (selectedCompany?.id) {
      cargarTodosLosEmpleados();
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany?.id && contratosActivados) {
      cargarResumenContratos();
    } else {
      setContratosPorEmpleado({});
    }
  }, [selectedCompany?.id, contratosActivados]);

  const cargarResumenContratos = async () => {
    const response = await contratosEmpleadoService.obtenerResumenPorEmpresa(selectedCompany.id);
    if (response.success) {
      setContratosPorEmpleado(response.data || {});
    }
  };

  // Aplicar filtros locales cuando cambien los criterios de búsqueda/filtrado
  useEffect(() => {
    aplicarFiltrosLocales();
  }, [empleadosCompletos, searchTerm, filterEstado, filterCargo, filterProyecto, sortField, sortDirection, currentPage]);

  // Función para cargar TODOS los empleados desde la API (una sola vez)
  const cargarTodosLosEmpleados = async () => {
    try {
      setLoading(true);
      setError('');

      // Cargar todos los empleados sin paginación ni filtros
      const params = {
        per_page: 1000, // Cargar muchos empleados de una vez
        empresa_id: selectedCompany.id
      };

      console.log('🔍 Cargando TODOS los empleados de la empresa:', selectedCompany.name);
      const response = await empleadoService.getAllEmpleados(params);
      
      if (response.success) {
        const todosLosEmpleados = response.data.data || [];
        setEmpleadosCompletos(todosLosEmpleados);
        console.log('✅ Empleados completos cargados:', todosLosEmpleados.length);
        
        // Los filtros se aplicarán automáticamente por el segundo useEffect
      } else {
        setError('Error al cargar empleados');
      }
    } catch (err) {
      console.error('❌ Error cargando empleados:', err);
      setError(err.message || 'Error al cargar empleados');
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros y búsqueda localmente
  const aplicarFiltrosLocales = () => {
    if (empleadosCompletos.length === 0) return;

    let empleadosFiltrados = [...empleadosCompletos];

    // Aplicar búsqueda por texto
    if (searchTerm && searchTerm.trim() !== '') {
      const termino = searchTerm.toLowerCase().trim();
      empleadosFiltrados = empleadosFiltrados.filter(empleado => {
        return (
          empleado.nombre_completo?.toLowerCase().includes(termino) ||
          empleado.numero_documento?.toLowerCase().includes(termino) ||
          empleado.email?.toLowerCase().includes(termino) ||
          empleado.telefono?.toLowerCase().includes(termino) ||
          empleado.cargo?.toLowerCase().includes(termino) ||
          empleado.categoria?.toLowerCase().includes(termino) ||
          empleado.proyecto?.toLowerCase().includes(termino)
        );
      });
    }

    // Aplicar filtro por estado
    if (filterEstado && filterEstado.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter(empleado => 
        empleado.estado === filterEstado
      );
    }

    // Aplicar filtro por cargo
    if (filterCargo && filterCargo.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter(empleado => 
        empleado.cargo === filterCargo
      );
    }

    // Aplicar filtro por proyecto
    if (filterProyecto && filterProyecto.trim() !== '') {
      empleadosFiltrados = empleadosFiltrados.filter(empleado => 
        empleado.proyecto === filterProyecto
      );
    }

    // Aplicar ordenamiento
    empleadosFiltrados.sort((a, b) => {
      let valorA = a[sortField] || '';
      let valorB = b[sortField] || '';
      
      // Convertir a string para comparación consistente
      valorA = valorA.toString().toLowerCase();
      valorB = valorB.toString().toLowerCase();
      
      if (sortDirection === 'asc') {
        return valorA.localeCompare(valorB);
      } else {
        return valorB.localeCompare(valorA);
      }
    });

    // Calcular paginación
    const totalFiltrados = empleadosFiltrados.length;
    const totalPaginas = Math.ceil(totalFiltrados / perPage) || 1;
    
    // Si la página actual es mayor al total de páginas, resetear a la primera página
    let paginaActual = currentPage;
    if (currentPage > totalPaginas) {
      paginaActual = 1;
      setCurrentPage(1);
    }
    
    const inicioIndice = (paginaActual - 1) * perPage;
    const finIndice = inicioIndice + perPage;
    const empleadosPaginados = empleadosFiltrados.slice(inicioIndice, finIndice);

    // Actualizar estados
    setEmpleados(empleadosPaginados);
    setTotalEmpleados(totalFiltrados);
    setTotalPages(totalPaginas);

    console.log('🔍 Filtros aplicados:', {
      termino: searchTerm,
      estado: filterEstado,
      cargo: filterCargo,
      proyecto: filterProyecto,
      orden: `${sortField} ${sortDirection}`,
      totalOriginal: empleadosCompletos.length,
      totalFiltrado: totalFiltrados,
      pagina: currentPage,
      totalPaginas,
      empleadosMostrados: empleadosPaginados.length
    });
  };

  // Función para buscar empleados (ahora funciona localmente)
  const buscarEmpleados = (termino) => {
    setSearchTerm(termino);
    setCurrentPage(1); // Resetear a la primera página
    // El filtrado se aplica automáticamente por el useEffect
  };

  // Función para cambiar filtros (ahora funciona localmente)
  const cambiarFiltro = (campo, valor) => {
    if (campo === 'estado') {
      setFilterEstado(valor);
    } else if (campo === 'cargo') {
      setFilterCargo(valor);
    } else if (campo === 'proyecto') {
      setFilterProyecto(valor);
    }
    setCurrentPage(1); // Resetear a la primera página
    // El filtrado se aplica automáticamente por el useEffect
  };

  // Función para cambiar ordenamiento (ahora funciona localmente)
  const cambiarOrdenamiento = (campo) => {
    if (sortField === campo) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(campo);
      setSortDirection('asc');
    }
    setCurrentPage(1);
    // El ordenamiento se aplica automáticamente por el useEffect
  };

  // Función para mostrar mensaje
  const mostrarMensaje = (tipo, texto) => {
    setMensaje({ visible: true, tipo, texto });
    setTimeout(() => {
      setMensaje({ visible: false, tipo: '', texto: '' });
    }, 5000);
  };

  // Función para abrir modal de baja
  const abrirModalBaja = (empleado) => {
    setEmpleadoParaBaja(empleado);
    setFechaBaja(new Date().toISOString().split('T')[0]); // Fecha actual por defecto
    setMotivoBaja('');
    setMotivoBajaDetalle('');
    setShowBajaModal(true);
  };

  // Función para dar de baja empleado
  const darDeBaja = async () => {
    if (!empleadoParaBaja || !fechaBaja || !motivoBaja) return;

    try {
      setProcesandoBaja(true);

      const motivoFinal = construirMotivoCese(motivoBaja, motivoBajaDetalle);
      
      // Crear registro en historial laboral (solo para baja)
      const historialData = {
        empleado_id: empleadoParaBaja.id,
        fecha_cese: fechaBaja,
        motivo_cese: motivoFinal
        // fecha_ingreso se obtiene automáticamente del historial del empleado en el backend
      };
      
      console.log('🔽 DATOS ENVIADOS PARA DAR DE BAJA:');
      console.log('📋 Empleado:', empleadoParaBaja.nombre_completo, '(ID:', empleadoParaBaja.id, ')');
      console.log('📅 Fecha de baja:', fechaBaja);
      console.log('📝 Motivo:', motivoFinal);
      console.log('🚀 Datos completos enviados al endpoint:', historialData);
      console.log('ℹ️ Nota: La fecha_ingreso se obtiene automáticamente del último historial activo del empleado');
      
      await empleadoService.createHistorialLaboral(historialData);
      
      mostrarMensaje('success', `Empleado "${empleadoParaBaja.nombre_completo}" dado de baja exitosamente`);
      
      // Recargar la lista
      await cargarTodosLosEmpleados();
      
      // Cerrar modal
      setShowBajaModal(false);
      setEmpleadoParaBaja(null);
      setFechaBaja('');
      setMotivoBaja('');
      setMotivoBajaDetalle('');
      
    } catch (err) {
      console.error('❌ Error dando de baja empleado:', err);
      mostrarMensaje('error', err.message || 'Error al dar de baja empleado');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para formatear fecha ISO a formato local
  const formatearFechaISO = (fechaISO) => {
    if (!fechaISO) return null;
    // Extraer solo la parte de la fecha (YYYY-MM-DD) sin la hora
    const fechaSolo = fechaISO.split('T')[0];
    const [year, month, day] = fechaSolo.split('-');
    return `${day}/${month}/${year}`;
  };

  // Función para abrir modal de historial
  const abrirModalHistorial = (empleado) => {
    setEmpleadoParaBaja(empleado);
    
    console.log('📋 Historial laboral original:', empleado.historial_laboral);
    
    // Formatear las fechas del historial antes de mostrarlas
    const historialFormateado = (empleado.historial_laboral || []).map((registro, index) => {
      console.log(`📅 Registro ${index}:`, {
        fecha_ingreso_original: registro.fecha_ingreso,
        fecha_cese_original: registro.fecha_cese,
        fecha_ingreso_formateada: formatearFechaISO(registro.fecha_ingreso),
        fecha_cese_formateada: registro.fecha_cese ? formatearFechaISO(registro.fecha_cese) : null
      });
      
      return {
        ...registro,
        fecha_ingreso_formateada: formatearFechaISO(registro.fecha_ingreso),
        fecha_cese_formateada: registro.fecha_cese ? formatearFechaISO(registro.fecha_cese) : null
      };
    });
    
    console.log('✅ Historial formateado:', historialFormateado);
    setHistorialEditable(historialFormateado);
    setShowHistorialModal(true);
  };

  // Función para dar de alta empleado
  const darDeAlta = async () => {
    if (!empleadoParaBaja || !fechaReingreso) return;

    try {
      setProcesandoBaja(true);
      
      // Crear nuevo registro en historial laboral para el reingreso
      const historialData = {
        empleado_id: empleadoParaBaja.id,
        fecha_ingreso: fechaReingreso,
        fecha_cese: null,
        motivo_cese: null
      };
      
      console.log('🔼 DATOS ENVIADOS PARA DAR DE ALTA:');
      console.log('📋 Empleado:', empleadoParaBaja.nombre_completo, '(ID:', empleadoParaBaja.id, ')');
      console.log('📅 Fecha de reingreso:', fechaReingreso);
      console.log('🚀 Datos completos enviados al endpoint:', historialData);
      
      await empleadoService.createHistorialLaboral(historialData);
      
      mostrarMensaje('success', `Empleado "${empleadoParaBaja.nombre_completo}" dado de alta exitosamente`);
      
      // Recargar la lista
      await cargarTodosLosEmpleados();
      
      // Cerrar modal
      setShowHistorialModal(false);
      setEmpleadoParaBaja(null);
      setFechaReingreso('');
      
    } catch (err) {
      console.error('❌ Error dando de alta empleado:', err);
      mostrarMensaje('error', err.message || 'Error al dar de alta empleado');
    } finally {
      setProcesandoBaja(false);
    }
  };

  const abrirModalEditarHistorial = (registro) => {
    if (!registro?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a editar');
      return;
    }

    setEditandoHistorial(registro);
    setFechaIngresoEdit((registro.fecha_ingreso || '').split('T')[0]);
    setFechaCeseEdit((registro.fecha_cese || '').split('T')[0]);
    setMotivoCeseEdit(motivoCeseEnLista(registro.motivo_cese || ''));
    setShowEditarHistorialModal(true);
  };

  const guardarEdicionHistorial = async () => {
    if (!empleadoParaBaja?.id || !editandoHistorial?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a actualizar');
      return;
    }

    if (!fechaIngresoEdit) {
      mostrarMensaje('error', 'La fecha de ingreso es obligatoria');
      return;
    }

    if (fechaCeseEdit && !motivoCeseEdit) {
      mostrarMensaje('error', 'Seleccione el motivo de cese');
      return;
    }

    try {
      setProcesandoBaja(true);

      const payload = {
        fecha_ingreso: fechaIngresoEdit,
        fecha_cese: fechaCeseEdit || null,
        motivo_cese: fechaCeseEdit ? (motivoCeseEdit || null) : null
      };

      await empleadoService.updateHistorialLaboral(empleadoParaBaja.id, editandoHistorial.id, payload);

      // Recargar empleados para reflejar cambios de estado Activo/Cesado
      const params = { per_page: 1000, empresa_id: selectedCompany.id };
      const response = await empleadoService.getAllEmpleados(params);
      const empleadosActualizados = response?.data?.data || [];
      setEmpleadosCompletos(empleadosActualizados);

      const empleadoActualizado = empleadosActualizados.find(emp => emp.id === empleadoParaBaja.id);
      if (empleadoActualizado) {
        const historialFormateado = (empleadoActualizado.historial_laboral || []).map((item) => ({
          ...item,
          fecha_ingreso_formateada: formatearFechaISO(item.fecha_ingreso),
          fecha_cese_formateada: item.fecha_cese ? formatearFechaISO(item.fecha_cese) : null
        }));

        setEmpleadoParaBaja(empleadoActualizado);
        setHistorialEditable(historialFormateado);
      }

      setShowEditarHistorialModal(false);
      setEditandoHistorial(null);
      setFechaIngresoEdit('');
      setFechaCeseEdit('');
      setMotivoCeseEdit('');
      mostrarMensaje('success', 'Registro de historial actualizado correctamente');
    } catch (err) {
      console.error('❌ Error actualizando historial laboral:', err);
      mostrarMensaje('error', err.message || 'Error al actualizar registro del historial');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para eliminar un registro del historial laboral
  const eliminarRegistroHistorial = async (registro) => {
    if (!empleadoParaBaja?.id || !registro?.id) {
      mostrarMensaje('error', 'No se pudo identificar el registro a eliminar');
      return;
    }

    const confirmar = window.confirm(
      '¿Seguro que deseas eliminar este registro del historial laboral? Esta acción no se puede deshacer.'
    );

    if (!confirmar) return;

    try {
      setProcesandoBaja(true);

      await empleadoService.deleteHistorialLaboral(empleadoParaBaja.id, registro.id);

      // Recargar empleados para reflejar el estado correcto (Activo/Cesado) luego de eliminar
      const params = { per_page: 1000, empresa_id: selectedCompany.id };
      const response = await empleadoService.getAllEmpleados(params);
      const empleadosActualizados = response?.data?.data || [];
      setEmpleadosCompletos(empleadosActualizados);

      const empleadoActualizado = empleadosActualizados.find(emp => emp.id === empleadoParaBaja.id);
      if (empleadoActualizado) {
        const historialFormateado = (empleadoActualizado.historial_laboral || []).map((item) => ({
          ...item,
          fecha_ingreso_formateada: formatearFechaISO(item.fecha_ingreso),
          fecha_cese_formateada: item.fecha_cese ? formatearFechaISO(item.fecha_cese) : null
        }));

        setEmpleadoParaBaja(empleadoActualizado);
        setHistorialEditable(historialFormateado);
      } else {
        setShowHistorialModal(false);
        setEmpleadoParaBaja(null);
        setHistorialEditable([]);
      }

      mostrarMensaje('success', 'Registro de historial eliminado correctamente');
    } catch (err) {
      console.error('❌ Error eliminando registro del historial laboral:', err);
      mostrarMensaje('error', err.message || 'Error al eliminar registro del historial');
    } finally {
      setProcesandoBaja(false);
    }
  };

  // Función para cancelar modal
  const cancelarModal = () => {
    setShowBajaModal(false);
    setShowHistorialModal(false);
    setShowReporteModal(false);
    setEmpleadoParaBaja(null);
    setEmpleadoParaReporte(null);
    setFechaBaja('');
    setMotivoBaja('');
    setMotivoBajaDetalle('');
    setFechaReingreso('');
    setHistorialEditable([]);
    setEditandoHistorial(null);
    setShowEditarHistorialModal(false);
    setFechaIngresoEdit('');
    setFechaCeseEdit('');
    setMotivoCeseEdit('');
  };

  // Función para abrir modal de reporte
  const abrirModalReporte = (empleado) => {
    setEmpleadoParaReporte(empleado);
    setShowReporteModal(true);
  };

  const abrirModalAsignarContrato = (empleado, soloHistorial = false) => {
    setEmpleadoParaContrato(empleado);
    setContratoSoloHistorial(soloHistorial);
    setShowAsignarContratoModal(true);
  };

  const cerrarModalAsignarContrato = () => {
    setShowAsignarContratoModal(false);
    setEmpleadoParaContrato(null);
    setContratoSoloHistorial(false);
  };

  // Función para generar PDF del reporte
  const generarReportePDF = async () => {
    if (!empleadoParaReporte) return;

    try {
      setGenerandoPDF(true);
      
      const elemento = document.getElementById('reporte-constancia');
      if (!elemento) {
        throw new Error('No se pudo encontrar el elemento del reporte');
      }

      // Configurar html2canvas para mejor calidad
      const canvas = await html2canvas(elemento, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calcular dimensiones para ajustar al PDF
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      // Agregar la imagen al PDF
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Si el contenido es más alto que una página, agregar páginas adicionales
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Generar nombre del archivo
      const tipoConstancia = empleadoParaReporte.estado === 'Activo' ? 'Alta' : 'Baja';
      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Constancia_${tipoConstancia}_${empleadoParaReporte.nombre_completo.replace(/\s+/g, '_')}_${fechaActual}.pdf`;

      // Descargar el PDF
      pdf.save(nombreArchivo);
      
      mostrarMensaje('success', 'Reporte PDF generado exitosamente');
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      mostrarMensaje('error', 'Error al generar el PDF: ' + error.message);
    } finally {
      setGenerandoPDF(false);
    }
  };

  // Función para obtener el último registro de historial laboral
  const obtenerUltimoHistorial = (empleado) => {
    if (!empleado.historial_laboral || empleado.historial_laboral.length === 0) {
      return null;
    }
    
    // Si el empleado está cesado, buscar el registro con fecha_cese más reciente
    if (empleado.estado === 'Cesado') {
      const registrosConCese = empleado.historial_laboral.filter(registro => registro.fecha_cese);
      if (registrosConCese.length > 0) {
        // Ordenar por fecha de cese descendente para obtener el más reciente
        const historialCeseOrdenado = [...registrosConCese].sort((a, b) => 
          new Date(b.fecha_cese) - new Date(a.fecha_cese)
        );
        return historialCeseOrdenado[0];
      }
    }
    
    // Para empleados activos o si no hay registros con cese, ordenar por fecha de ingreso
    const historialOrdenado = [...empleado.historial_laboral].sort((a, b) => 
      new Date(b.fecha_ingreso) - new Date(a.fecha_ingreso)
    );
    
    return historialOrdenado[0];
  };

  // Función para formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    
    // Extraer solo la parte de la fecha (YYYY-MM-DD) sin considerar zona horaria
    const fechaStr = fecha.split('T')[0];
    const [year, month, day] = fechaStr.split('-');
    
    // Crear fecha sin ajustes de zona horaria
    return `${day}/${month}/${year}`;
  };

  // Función para formatear salario
  const formatearSalario = (salario) => {
    if (!salario) return '-';
    return `S/ ${parseFloat(salario).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`;
  };

  // Obtener listas únicas para filtros (desde todos los empleados, no solo los filtrados)
  const cargosUnicos = [...new Set(empleadosCompletos.map(emp => emp.cargo).filter(Boolean))];
  const proyectosUnicos = [...new Set(empleadosCompletos.map(emp => emp.proyecto).filter(Boolean))];
  const estadosUnicos = ['Activo', 'Cesado'];

  // Función para obtener iniciales del nombre
  const obtenerIniciales = (nombreCompleto) => {
    if (!nombreCompleto) return '??';
    return nombreCompleto
      .split(' ')
      .slice(0, 2)
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase();
  };

  // Función para abrir modal de importación
  const abrirModalImportacion = () => {
    setShowImportarModal(true);
  };

  // Función para manejar el éxito de la importación
  const handleImportSuccess = (resultados) => {
    console.log('✅ Importación exitosa:', resultados);
    
    // Mostrar mensaje de éxito
    const empleadosImportados = resultados.exitosos.length;
    const empleadosFallidos = resultados.fallidos.length;
    
    let mensaje = `${empleadosImportados} empleado(s) importado(s) exitosamente`;
    if (empleadosFallidos > 0) {
      mensaje += ` (${empleadosFallidos} fallido(s))`;
    }
    
    mostrarMensaje('success', mensaje);
    
    // Recargar la lista de empleados
    cargarTodosLosEmpleados();
    
    // Cerrar modal
    setShowImportarModal(false);
  };

  // Función para exportar empleados a Excel
  const exportarEmpleadosExcel = async () => {
    try {
      mostrarMensaje('success', 'Preparando descarga del archivo Excel...');
      
      // Obtener todos los empleados sin paginación para la exportación
      const params = {
        empresa_id: selectedCompany.id,
        search: searchTerm || undefined,
        estado: filterEstado || undefined,
        cargo: filterCargo || undefined,
        proyecto: filterProyecto || undefined,
        sort_field: sortField,
        sort_direction: sortDirection,
        per_page: 1000 // Obtener muchos registros para exportar
      };

      console.log('📋 Exportando empleados con parámetros:', params);
      const response = await empleadoService.getAllEmpleados(params);
      
      if (!response.success || !response.data.data) {
        throw new Error('No se pudieron obtener los datos de empleados');
      }

      const empleadosParaExportar = response.data.data;
      console.log(`📊 Exportando ${empleadosParaExportar.length} empleados`);

      // Preparar datos para Excel
      const datosExcel = empleadosParaExportar.map((empleado, index) => {
        const ultimoHistorial = obtenerUltimoHistorial(empleado);
        
        // Log para debugging (eliminar después)
        if (index === 0) {
          console.log('📊 Estructura de empleado para exportación:', {
            afp: empleado.afp,
            afp_nombre: empleado.afp_nombre,
            tipo_afp: empleado.tipo_afp,
            sistema_pensionario: empleado.sistema_pensionario,
            cuspp: empleado.cuspp,
            banco: empleado.banco,
            cuenta_bancaria: empleado.cuenta_bancaria,
            cci: empleado.cci
          });
        }
        
        // Obtener nombre de AFP
        const nombreAfp = empleado.afp_nombre || empleado.afp?.nombre || empleado.afp || '';
        
        return {
          'N°': index + 1,
          'Nombre Completo': empleado.nombre_completo || '',
          'Tipo Documento': empleado.tipo_documento || '',
          'Número Documento': empleado.numero_documento || '',
          'Email': empleado.email || '',
          'Teléfono': empleado.telefono || '',
          'Cargo': empleado.cargo || '',
          'Categoría': empleado.categoria || '',
          'Área de Trabajo': empleado.areas_de_trabajo || '',
          'Proyecto': empleado.proyecto || '',
          'Tipo Contratación': empleado.tipo_contratacion || '',
          'Estado': empleado.estado || '',
          'Régimen Laboral': empleado.regimen_laboral_nombre || empleado.regimen_laboral?.nombre || empleado.regimen_laboral || '',
          'Sueldo Básico': empleado.sueldo_basico ? parseFloat(empleado.sueldo_basico) : 0,
          'Hora Entrada': empleado.hora_entrada || '',
          'Hora Salida': empleado.hora_salida || '',
          'Sistema Pensionario': empleado.sistema_pensionario || empleado.tipo_aporte || (nombreAfp ? 'AFP' : 'ONP'),
          'AFP': nombreAfp,
          'Tipo AFP': empleado.tipo_afp || empleado.afp?.tipo_afp || empleado.comision_tipo || '',
          'CUSPP': empleado.cuspp || empleado.codigo_cuspp || empleado.afp?.cuspp || '',
          'Banco': empleado.banco || '',
          'Número Cuenta': empleado.cuenta_bancaria || '',
          'CCI': empleado.cci || '',
          'Contacto Emergencia': empleado.contacto_emergencia || '',
          'Teléfono Emergencia': empleado.telefono_emergencia || '',
          'Fecha Ingreso': ultimoHistorial?.fecha_ingreso ? formatearFecha(ultimoHistorial.fecha_ingreso) : '',
          'Fecha Cese': ultimoHistorial?.fecha_cese ? formatearFecha(ultimoHistorial.fecha_cese) : '',
          'Motivo Cese': ultimoHistorial?.motivo_cese || '',
          'Fecha Nacimiento': empleado.fecha_nacimiento ? formatearFecha(empleado.fecha_nacimiento) : '',
          'Sexo': empleado.sexo || '',
          'Estado Civil': empleado.estado_civil || '',
          'Número Hijos': empleado.numero_hijos || 0,
          'Dirección': empleado.direccion || '',
          'Distrito': empleado.distrito || '',
          'Provincia': empleado.provincia || '',
          'Departamento': empleado.departamento || ''
        };
      });

      // Crear workbook y worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(datosExcel);

      // Configurar ancho de columnas
      const columnWidths = [
        { wch: 5 },   // N°
        { wch: 25 },  // Nombre Completo
        { wch: 12 },  // Tipo Documento
        { wch: 15 },  // Número Documento
        { wch: 25 },  // Email
        { wch: 15 },  // Teléfono
        { wch: 20 },  // Cargo
        { wch: 15 },  // Categoría
        { wch: 18 },  // Área de Trabajo
        { wch: 20 },  // Proyecto
        { wch: 15 },  // Tipo Contratación
        { wch: 10 },  // Estado
        { wch: 15 },  // Régimen Laboral
        { wch: 12 },  // Sueldo Básico
        { wch: 12 },  // Hora Entrada
        { wch: 12 },  // Hora Salida
        { wch: 15 },  // Sistema Pensionario
        { wch: 15 },  // AFP
        { wch: 12 },  // Tipo AFP
        { wch: 18 },  // CUSPP
        { wch: 20 },  // Banco
        { wch: 18 },  // Número Cuenta
        { wch: 20 },  // CCI
        { wch: 25 },  // Contacto Emergencia
        { wch: 15 },  // Teléfono Emergencia
        { wch: 12 },  // Fecha Ingreso
        { wch: 12 },  // Fecha Cese
        { wch: 20 },  // Motivo Cese
        { wch: 15 },  // Fecha Nacimiento
        { wch: 10 },  // Sexo
        { wch: 12 },  // Estado Civil
        { wch: 10 },  // Número Hijos
        { wch: 30 },  // Dirección
        { wch: 15 },  // Distrito
        { wch: 15 },  // Provincia
        { wch: 15 }   // Departamento
      ];
      
      worksheet['!cols'] = columnWidths;

      // Agregar worksheet al workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Empleados');

      // Generar nombre del archivo
      const fechaActual = new Date().toISOString().split('T')[0];
      const nombreEmpresa = selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '_');
      const nombreArchivo = `Empleados_${nombreEmpresa}_${fechaActual}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(workbook, nombreArchivo);

      console.log(`✅ Archivo Excel generado: ${nombreArchivo}`);
      mostrarMensaje('success', `Archivo Excel descargado: ${nombreArchivo}`);

    } catch (error) {
      console.error('❌ Error exportando a Excel:', error);
      mostrarMensaje('error', 'Error al generar el archivo Excel: ' + error.message);
    }
  };

  // Si no hay empresa seleccionada
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver la lista de empleados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-bold text-gray-900">Empleados</h1>
          <p className="mt-2 text-sm text-gray-700">
            Lista de empleados de {selectedCompany.name} ({totalEmpleados} empleados)
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <div className="flex space-x-3">
            <button
              onClick={exportarEmpleadosExcel}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 sm:w-auto"
              title="Exportar empleados a Excel"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar
            </button>
            <button
              onClick={abrirModalImportacion}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Importar
            </button>
            <Link
              to={nuevoEmpleadoPath}
              className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Agregar Empleado
            </Link>
          </div>
        </div>
      </div>

      {/* Mensajes */}
      {mensaje.visible && (
        <div className={`mb-6 p-4 rounded-md ${
          mensaje.tipo === 'success' ? 'bg-green-100 text-green-700 border border-green-300' : 
          'bg-red-100 text-red-700 border border-red-300'
        }`}>
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mensaje.tipo === 'success' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              )}
            </svg>
            {mensaje.texto}
          </div>
        </div>
      )}

      {/* Filtros y búsqueda */}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {/* Búsqueda */}
        <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Buscar empleado
          </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
          <input
            type="text"
            id="search"
            value={searchTerm}
                onChange={(e) => buscarEmpleados(e.target.value)}
                placeholder="Buscar por nombre, documento, email..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
        </div>
          </div>

          {/* Filtro por estado */}
          <div>
            <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              id="estado"
              value={filterEstado}
              onChange={(e) => cambiarFiltro('estado', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los estados</option>
              {estadosUnicos.map(estado => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
          </div>

          {/* Filtro por cargo */}
        <div>
            <label htmlFor="cargo" className="block text-sm font-medium text-gray-700 mb-1">
              Cargo
          </label>
          <select
              id="cargo"
              value={filterCargo}
              onChange={(e) => cambiarFiltro('cargo', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los cargos</option>
              {cargosUnicos.map(cargo => (
                <option key={cargo} value={cargo}>{cargo}</option>
            ))}
          </select>
          </div>

          {/* Filtro por proyecto */}
          <div>
            <label htmlFor="proyecto" className="block text-sm font-medium text-gray-700 mb-1">
              Proyecto
            </label>
            <select
              id="proyecto"
              value={filterProyecto}
              onChange={(e) => cambiarFiltro('proyecto', e.target.value)}
              className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos los proyectos</option>
              {proyectosUnicos.map(proyecto => (
                <option key={proyecto} value={proyecto}>{proyecto}</option>
              ))}
            </select>
          </div>

          {/* Botón limpiar filtros */}
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterEstado('');
                setFilterCargo('');
                setFilterProyecto('');
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de empleados */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Cargando empleados...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-red-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar empleados</h3>
            <p className="text-gray-500 text-center mb-4">{error}</p>
            <button
              onClick={cargarTodosLosEmpleados}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Reintentar
            </button>
          </div>
        ) : empleados.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="text-gray-400 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empleados</h3>
            <p className="text-gray-500 text-center mb-4">
              {searchTerm || filterEstado || filterCargo || filterProyecto
                ? 'No se encontraron empleados que coincidan con los filtros.' 
                : 'Aún no hay empleados registrados en esta empresa.'
              }
            </p>
            {!searchTerm && !filterEstado && !filterCargo && !filterProyecto && (
              <Link
                to={nuevoEmpleadoPath}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Agregar primer empleado
              </Link>
            )}
          </div>
        ) : (
          <>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('nombre_completo')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Empleado</span>
                        {sortField === 'nombre_completo' && (
                          <svg className={`w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('cargo')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Información Laboral</span>
                        {sortField === 'cargo' && (
                          <svg className={`w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('sueldo_basico')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Sueldo</span>
                        {sortField === 'sueldo_basico' && (
                          <svg className={`w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Ingreso
                </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => cambiarOrdenamiento('estado')}
                    >
                      <div className="flex items-center space-x-1">
                        <span>Estado</span>
                        {sortField === 'estado' && (
                          <svg className={`w-4 h-4 ${sortDirection === 'asc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        )}
                      </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                  {empleados.map((empleado) => (
                <tr key={empleado.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center">
                              <span className="text-sm font-medium text-white">
                                {obtenerIniciales(empleado.nombre_completo)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                              {empleado.nombre_completo}
                        </div>
                            <div className="text-sm text-gray-500">
                              {empleado.tipo_documento}: {empleado.numero_documento}
                            </div>
                            {empleado.email && (
                        <div className="text-sm text-gray-500">
                          {empleado.email}
                        </div>
                            )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 w-48 max-w-48">
                        <div className="text-sm text-gray-900 font-medium truncate overflow-hidden" title={empleado.cargo}>
                          {empleado.cargo}
                        </div>
                        <div className="text-sm text-gray-500 truncate overflow-hidden" title={empleado.categoria}>
                          {empleado.categoria}
                        </div>
                        <div className="text-sm text-gray-500 truncate overflow-hidden" title={empleado.proyecto}>
                          {empleado.proyecto}
                        </div>
                  </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatearSalario(empleado.sueldo_basico)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {empleado.historial_laboral?.[0]?.fecha_ingreso 
                          ? formatearFecha(empleado.historial_laboral[0].fecha_ingreso)
                          : '-'
                        }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      empleado.estado === 'Activo' 
                        ? 'bg-green-100 text-green-800' 
                        : empleado.estado === 'Cesado'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {empleado.estado}
                    </span>
                  </td>
                      <td className="px-6 py-4 text-xs font-medium">
                        <div className="flex flex-col space-y-2">
                    <Link
                      to={pathWithEmpresa(`/empleados/${empleado.id}/editar`, empresaActivaId)}
                            className="text-yellow-600 hover:text-yellow-900 flex items-center space-x-1"
                    >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Editar</span>
                    </Link>
                          {/* Botón de Reporte */}
                          <button
                            onClick={() => abrirModalReporte(empleado)}
                            className="text-purple-600 hover:text-purple-900 flex items-center space-x-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Reporte</span>
                          </button>

                          {contratosActivados && empleado.estado === 'Activo' && (
                            <button
                              onClick={() => abrirModalAsignarContrato(empleado, false)}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center space-x-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span>
                                {contratosPorEmpleado[empleado.id]
                                  ? `Contratos (${contratosPorEmpleado[empleado.id]})`
                                  : 'Asignar contrato'}
                              </span>
                            </button>
                          )}

                          {contratosActivados && empleado.estado === 'Cesado' && (
                            <button
                              onClick={() => abrirModalAsignarContrato(empleado, true)}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center space-x-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>
                                Historial de contratos
                                {contratosPorEmpleado[empleado.id]
                                  ? ` (${contratosPorEmpleado[empleado.id]})`
                                  : ''}
                              </span>
                            </button>
                          )}
                          
                          {/* Botón de Baja/Alta según estado */}
                          {empleado.estado === 'Activo' ? (
                            <button
                              onClick={() => abrirModalBaja(empleado)}
                              className="text-orange-600 hover:text-orange-900 flex items-center space-x-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                              </svg>
                              <span>Dar de Baja</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => abrirModalHistorial(empleado)}
                              className="text-green-600 hover:text-green-900 flex items-center space-x-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                              <span>Historial</span>
                            </button>
                          )}
                        </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando <span className="font-medium">{((currentPage - 1) * perPage) + 1}</span> a{' '}
                      <span className="font-medium">{Math.min(currentPage * perPage, totalEmpleados)}</span> de{' '}
                      <span className="font-medium">{totalEmpleados}</span> empleados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      
                      {/* Números de página */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Siguiente</span>
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de dar de baja */}
      {showBajaModal && empleadoParaBaja && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4 text-center">Dar de Baja Empleado</h3>
              <div className="mt-4">
                <p className="text-sm text-gray-600 text-center mb-4">
                  <span className="font-medium text-gray-900">{empleadoParaBaja.nombre_completo}</span>
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Baja *
                    </label>
                    <input
                      type="date"
                      value={fechaBaja}
                      onChange={(e) => setFechaBaja(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motivo de Baja *
                    </label>
                    <select
                      value={motivoBaja}
                      onChange={(e) => {
                        setMotivoBaja(e.target.value);
                        if (e.target.value !== MOTIVO_CESE_OTROS) {
                          setMotivoBajaDetalle('');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    >
                      <option value="">Seleccionar motivo</option>
                      {MOTIVOS_CESE.map((motivo) => (
                        <option key={motivo} value={motivo}>
                          {motivo}
                        </option>
                      ))}
                    </select>
                  </div>

                  {motivoBaja === MOTIVO_CESE_OTROS && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Especificar motivo <span className="text-gray-400 font-normal">(opcional)</span>
                      </label>
                      <input
                        type="text"
                        value={motivoBajaDetalle}
                        onChange={(e) => setMotivoBajaDetalle(e.target.value)}
                        placeholder="Ej. Mutuo acuerdo, traslado de sede..."
                        maxLength={255}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-4 mt-6">
                <button
                  onClick={cancelarModal}
                  disabled={procesandoBaja}
                  className="px-4 py-2 bg-white text-gray-500 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={darDeBaja}
                  disabled={procesandoBaja || !fechaBaja || !motivoBaja}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md text-sm font-medium hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 flex items-center"
                >
                  {procesandoBaja ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Procesando...
                    </>
                  ) : (
                    'Dar de Baja'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de historial laboral */}
      {showHistorialModal && empleadoParaBaja && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Historial Laboral - {empleadoParaBaja.nombre_completo}
                </h3>
                <button
                  onClick={cancelarModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Tabla de historial */}
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha Ingreso
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha Cese
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Motivo Cese
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historialEditable.map((registro, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {registro.fecha_ingreso_formateada || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {registro.fecha_cese_formateada || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {registro.motivo_cese || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            !registro.fecha_cese 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {!registro.fecha_cese ? 'Activo' : 'Cesado'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => abrirModalEditarHistorial(registro)}
                              disabled={procesandoBaja || !registro?.id}
                              className="px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Editar registro"
                            >
                              Editar
                            </button>
                            {index === 0 ? (
                              <button
                                onClick={() => eliminarRegistroHistorial(registro)}
                                disabled={procesandoBaja || !registro?.id}
                                className="px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Eliminar registro"
                              >
                                Borrar
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Sección para dar de alta */}
              {empleadoParaBaja.estado !== 'Activo' && (
                <div className="bg-green-50 p-4 rounded-md">
                  <h4 className="text-md font-medium text-green-800 mb-3">Dar de Alta</h4>
                  <div className="flex items-end space-x-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-green-700 mb-1">
                        Fecha de Reingreso *
                      </label>
                      <input
                        type="date"
                        value={fechaReingreso}
                        onChange={(e) => setFechaReingreso(e.target.value)}
                        className="w-full px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        required
                      />
                    </div>
                    <button
                      onClick={darDeAlta}
                      disabled={procesandoBaja || !fechaReingreso}
                      className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 flex items-center"
                    >
                      {procesandoBaja ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Procesando...
                        </>
                      ) : (
                        'Dar de Alta'
                      )}
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={cancelarModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de historial laboral */}
      {showEditarHistorialModal && editandoHistorial && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Editar Registro de Historial Laboral</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Ingreso *
                  </label>
                  <input
                    type="date"
                    value={fechaIngresoEdit}
                    onChange={(e) => setFechaIngresoEdit(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha Cese (opcional)
                  </label>
                  <input
                    type="date"
                    value={fechaCeseEdit}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFechaCeseEdit(val);
                      if (!val) setMotivoCeseEdit('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si dejas fecha de cese en blanco, el empleado quedará activo.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo Cese {fechaCeseEdit ? '*' : '(opcional)'}
                  </label>
                  <select
                    value={motivoCeseEdit}
                    onChange={(e) => setMotivoCeseEdit(e.target.value)}
                    disabled={!fechaCeseEdit}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    <option value="">Seleccionar motivo</option>
                    {MOTIVOS_CESE.map((motivo) => (
                      <option key={motivo} value={motivo}>
                        {motivo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditarHistorialModal(false);
                    setEditandoHistorial(null);
                    setFechaIngresoEdit('');
                    setFechaCeseEdit('');
                    setMotivoCeseEdit('');
                  }}
                  disabled={procesandoBaja}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={guardarEdicionHistorial}
                  disabled={procesandoBaja || !fechaIngresoEdit || (fechaCeseEdit && !motivoCeseEdit)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {procesandoBaja ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de reporte/constancia */}
      {showReporteModal && empleadoParaReporte && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Constancia de {empleadoParaReporte.estado === 'Activo' ? 'Alta' : 'Baja'} Laboral
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={generarReportePDF}
                    disabled={generandoPDF}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 flex items-center"
                  >
                    {generandoPDF ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generando PDF...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Exportar PDF
                      </>
                    )}
                  </button>
                  <button
                    onClick={cancelarModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Contenido de la constancia */}
              <div id="reporte-constancia" className="bg-white p-8 border-2 border-gray-200 rounded-lg">
                {/* Encabezado de la empresa */}
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">
                    {obtenerNombreEmpresa(selectedCompany)}
                  </h1>
                  <p className="text-gray-600">
                    RUC: {selectedCompany?.ruc || 'N/A'}
                  </p>
                  {obtenerDetalleEmpresa(selectedCompany) && (
                    <p className="text-gray-600">
                      {obtenerDetalleEmpresa(selectedCompany)}
                    </p>
                  )}
                </div>

                {/* Título de la constancia */}
                <div className="text-center mb-8">
                  <h2 className="text-xl font-bold text-gray-800 mb-4">
                    CONSTANCIA DE {empleadoParaReporte.estado === 'Activo' ? 'ALTA' : 'BAJA'} LABORAL
                  </h2>
                  <div className="w-24 h-1 bg-purple-600 mx-auto"></div>
                </div>

                {/* Contenido principal */}
                <div className="space-y-6 text-justify leading-relaxed">
                  <p className="text-gray-700">
                    Por medio de la presente, se deja constancia que el(la) Sr(a). <strong>{empleadoParaReporte.nombre_completo}</strong>, 
                    identificado(a) con {empleadoParaReporte.tipo_documento}: <strong>{empleadoParaReporte.numero_documento}</strong>, 
                    {empleadoParaReporte.estado === 'Activo' ? ' se encuentra laborando' : ' cesó en sus funciones'} en nuestra empresa.
                  </p>

                  {/* Información del empleado */}
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-4">DATOS DEL EMPLEADO:</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Nombre completo:</span>
                        <p className="text-gray-600">{empleadoParaReporte.nombre_completo}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Documento:</span>
                        <p className="text-gray-600">{empleadoParaReporte.tipo_documento}: {empleadoParaReporte.numero_documento}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Cargo:</span>
                        <p className="text-gray-600">{empleadoParaReporte.cargo}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Categoría:</span>
                        <p className="text-gray-600">{empleadoParaReporte.categoria}</p>
                      </div>
                      {empleadoParaReporte.proyecto && (
                        <div>
                          <span className="font-medium text-gray-700">Proyecto:</span>
                          <p className="text-gray-600">{empleadoParaReporte.proyecto}</p>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-700">Estado actual:</span>
                        <p className="text-gray-600">{empleadoParaReporte.estado}</p>
                      </div>
                    </div>
                  </div>

                  {/* Información del historial laboral */}
                  {(() => {
                    const ultimoHistorial = obtenerUltimoHistorial(empleadoParaReporte);
                    if (ultimoHistorial) {
                      return (
                        <div className="bg-blue-50 p-6 rounded-lg">
                          <h3 className="font-semibold text-gray-800 mb-4">INFORMACIÓN LABORAL:</h3>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="font-medium text-gray-700">Fecha de ingreso:</span>
                              <p className="text-gray-600">{formatearFechaISO(ultimoHistorial.fecha_ingreso)}</p>
                            </div>
                            {ultimoHistorial.fecha_cese && (
                              <>
                                <div>
                                  <span className="font-medium text-gray-700">Fecha de cese:</span>
                                  <p className="text-gray-600">{formatearFechaISO(ultimoHistorial.fecha_cese)}</p>
                                </div>
                                <div className="col-span-2">
                                  <span className="font-medium text-gray-700">Motivo de cese:</span>
                                  <p className="text-gray-600">{ultimoHistorial.motivo_cese}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Párrafo de cierre */}
                  <p className="text-gray-700">
                    Se expide la presente constancia a solicitud del interesado(a) para los fines que estime conveniente.
                  </p>
                </div>

                {/* Pie de página */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex justify-between items-end">
                    <div className="text-left">
                      <p className="text-sm text-gray-600">
                        Fecha de emisión: {new Date().toLocaleDateString('es-PE', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="border-t border-gray-400 w-48 mb-2"></div>
                      <p className="text-sm font-medium text-gray-700">
                        Recursos Humanos
                      </p>
                      <p className="text-xs text-gray-600">
                        {obtenerNombreEmpresa(selectedCompany)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={cancelarModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de importación */}
      {showImportarModal && (
        <ImportarEmpleados
          onClose={() => setShowImportarModal(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}

      {showAsignarContratoModal && empleadoParaContrato && (
        <AsignarContratoModal
          isOpen={showAsignarContratoModal}
          onClose={cerrarModalAsignarContrato}
          empleado={empleadoParaContrato}
          empresa={selectedCompany}
          empresaId={selectedCompany?.id}
          soloHistorial={contratoSoloHistorial}
          onSuccess={(mensaje) => {
            mostrarMensaje('success', mensaje || 'Operación completada');
            cargarResumenContratos();
          }}
        />
      )}
    </div>
  );
};

export default Empleados; 